"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.alertAsOfSchema = exports.asOfAttemptSchema = exports.asOfResolutionSchema = exports.asOfFacilityProfileSchema = exports.asOfBandSetSchema = exports.alertHistorySchema = exports.alertTransitionSchema = exports.alertHistoryParamsSchema = void 0;
// The trail of every attempted transition on an alert (`RPM-018`, `J-272`).
//
// EVERY ATTEMPT, NOT EVERY CHANGE. `RPM-018`'s MUST: "Every attempted state transition on an alert
// writes one append-only row carrying the from state, the event, the outcome (`TRANSITIONED`,
// `REFUSED`, `IGNORED`), the actor, the instant, and the configuration versions in effect. A
// **refusal is recorded**, not discarded: 'the system would not let me' is the most useful line in a
// six-week-old investigation."
//
// THE READ IS A QUERY OVER PATIENT DATA. An alert is a workup on a resident, so the route carries
// `config.phi` and the row a caller cannot reach is `403` with an `access_denied` row — never a
// filtered list that quietly returns fewer rows.
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_reference_js_1 = require("./rpm-reference.js");
exports.alertHistoryParamsSchema = zod_1.z.strictObject({ alertId: zod_1.z.uuid() });
exports.alertTransitionSchema = zod_1.z
    .strictObject({
    /** Contiguous from 1 per alert (`uq_alert_transition_seq`, `ast_seq_positive`). */
    sequenceNo: zod_1.z.number().int(),
    fromState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    toState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    /** What was attempted, in the state machine's own word: `classify`, `supersede`, `close`. */
    event: zod_1.z.string(),
    outcome: zod_1.z.enum(rpm_enums_js_1.transitionOutcome.enumValues),
    /**
     * Why it did not happen. Null only on a `TRANSITIONED` row, which `ast_reason_required` enforces:
     * "a refusal or an ignore without a reason records that something did not happen and not why".
     */
    reason: zod_1.z.string().nullable(),
    /** The instant, as stored. The facility's own zone is `RPM-021`'s and is not applied here. */
    occurredAt: zod_1.z.iso.datetime(),
    /** WHO, as an account id. `RPM-083`: the catalogue carries no actor's name as text. */
    actorUserId: zod_1.z.uuid(),
    /**
     * The account's name, joined outward at read time from `user` for the History tab's actor column
     * (`RPM-018`'s Operator surface, `J-290`). The trail stores the id alone; this is a reading of it,
     * and null where the account has no name to show.
     */
    actorName: zod_1.z.string().nullable(),
})
    .meta({ id: "AlertTransition", description: "One attempted transition, recorded either way" });
exports.alertHistorySchema = zod_1.z
    .strictObject({
    rpmAlertId: zod_1.z.uuid(),
    /** The state the alert stands in now, so the trail can be read against where it ended up. */
    alertState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    /**
     * The configuration versions the alert was resolved against (`RPM-018`'s Schema row names
     * "`rpm_alerts` (the pinned version columns)"): the band set and the facility profile, the two
     * pins CR-43 leaves (`RPM-056`); the routing is the alert's resolution log, not a pin. A trail
     * read six weeks later is read against the thresholds that were in force, not today's.
     */
    classificationBandSetId: zod_1.z.uuid().nullable(),
    rpmFacilityProfileId: zod_1.z.uuid().nullable(),
    /** In `sequence_no` order, which is the order they were attempted. */
    transitions: zod_1.z.array(exports.alertTransitionSchema),
})
    .meta({
    id: "AlertHistory",
    description: "How this alert reached its current state, without replaying any code",
});
// ---------------------------------------------------------------------------------------------------
// What governed the alert, as of then (`RPM-056`, `J-290`).
//
// `GET /api/rpm/v1/alerts/:alertId/as-of` "returns the alert with the exact band rows and profile
// version that governed it, and every routing resolution logged with it". THE ANSWER IS READ, NEVER
// REPLAYED: the band rows are the pinned set's own (a published set is immutable, `RPM-058`), the
// zone is the pinned profile's, and the routing is the resolution log as resolved — the steps and the
// recipients stored on the row, so a later change to the directory moves nothing here (`CR-43`,
// `03-RPM-SCHEMA.md` §9.4 and the worked example of §10.6).
// ---------------------------------------------------------------------------------------------------
/**
 * One band set that governed the alert: the set the alert pinned when it opened, and any other set a
 * reading of the alert was scored under (`03-RPM-SCHEMA.md` §9.4, "Which thresholds classified this
 * reading?"). With its exact rows, in the protocol table's order.
 */
exports.asOfBandSetSchema = zod_1.z
    .strictObject({
    classificationBandSetId: zod_1.z.uuid(),
    bandSetVersion: zod_1.z.number().int(),
    label: zod_1.z.string(),
    sourceDocumentRef: zod_1.z.string(),
    effectiveFrom: zod_1.z.iso.datetime(),
    /** Null while the set is the one in force; set once a successor closed it. */
    effectiveTo: zod_1.z.iso.datetime().nullable(),
    /** True for the set `rpm_alerts.classification_band_set_id` names. */
    pinnedByAlert: zod_1.z.boolean(),
    /** How many of the alert's readings carry this set as the one that scored them. */
    readingCount: zod_1.z.number().int().min(0),
    bands: zod_1.z.array(rpm_reference_js_1.classificationBandSchema),
})
    .meta({ id: "AsOfBandSet", description: "A band set that governed the alert, with its rows" });
/** The facility profile version the alert pinned: the building's zone as it was then. */
exports.asOfFacilityProfileSchema = zod_1.z
    .strictObject({
    rpmFacilityProfileId: zod_1.z.uuid(),
    ianaTimeZone: zod_1.z.string(),
    validFrom: zod_1.z.iso.datetime(),
    validTo: zod_1.z.iso.datetime().nullable(),
    /** Set once a later version superseded this one. */
    supersededAt: zod_1.z.iso.datetime().nullable(),
})
    .meta({
    id: "AsOfFacilityProfile",
    description: "The facility profile version the alert pinned",
});
/** One recipient of a step, AS RESOLVED — the value and label stored on the resolution row. */
const asOfRecipientSchema = zod_1.z.object({
    value: zod_1.z.string(),
    label: zod_1.z.string(),
    channel: zod_1.z.enum(["EMAIL", "PHONE"]),
    extension: zod_1.z.string().nullable(),
});
/** One step of a logged resolution, as resolved. Where it stands now is the routing panel's. */
const asOfStepSchema = zod_1.z.object({
    order: zod_1.z.number().int(),
    method: zod_1.z.enum(["CALL", "EMAIL", "TEXT", "DAILY_SUMMARY", "EMR_ONLY"]),
    trigger: zod_1.z.enum(["ALWAYS", "ON_NO_ANSWER"]),
    due: zod_1.z.enum(["NOW", "AFTER_TWO_UNANSWERED_CALLS", "AT_NEXT_DAILY_SUMMARY", "NONE"]),
    to: zod_1.z.array(asOfRecipientSchema),
    cc: zod_1.z.array(asOfRecipientSchema),
});
/** One routing resolution of the alert, as it was logged (`rpm_alert_routing_resolutions`). */
exports.asOfResolutionSchema = zod_1.z
    .strictObject({
    routingResolutionId: zod_1.z.uuid(),
    /** Contiguous from 1 per alert (`uq_routing_resolution_seq`). */
    sequenceNo: zod_1.z.number().int(),
    resolvedAt: zod_1.z.iso.datetime(),
    resolvedBy: zod_1.z.uuid(),
    /** The account's name, joined outward at read time; null where it has none. */
    resolvedByName: zod_1.z.string().nullable(),
    /** The building's wall clock when it resolved, and the segment of the day. */
    clock: zod_1.z.object({
        zone: zod_1.z.string(),
        date: zod_1.z.string(),
        hour: zod_1.z.number().int(),
        minute: zod_1.z.number().int(),
        segment: zod_1.z.enum(["WEEKEND", "DAY_9_5", "NIGHT"]),
    }),
    status: zod_1.z.enum(rpm_enums_js_1.routingResolutionStatus.enumValues),
    reason: zod_1.z.string().nullable(),
    behaviour: zod_1.z.string().nullable(),
    /** The building's routing notes as the nurse was shown them, verbatim. */
    instruction: zod_1.z.string(),
    holdReason: zod_1.z.string().nullable(),
    steps: zod_1.z.array(asOfStepSchema),
})
    .meta({ id: "AsOfResolution", description: "One routing resolution of the alert, as resolved" });
/** One attempt, and the resolution and the step it was made against (`RPM-070` rule 9). */
exports.asOfAttemptSchema = zod_1.z
    .strictObject({
    notificationAttemptId: zod_1.z.number().int(),
    sequenceNo: zod_1.z.number().int(),
    attemptedAt: zod_1.z.iso.datetime(),
    attemptedBy: zod_1.z.uuid(),
    attemptedByName: zod_1.z.string().nullable(),
    channel: zod_1.z.enum(rpm_enums_js_1.notifyChannel.enumValues),
    outcome: zod_1.z.enum(rpm_enums_js_1.attemptOutcome.enumValues),
    /** As used: the verbatim value is the record (`RPM-026`). */
    destinationValue: zod_1.z.string(),
    destinationLabel: zod_1.z.string().nullable(),
    /** Both or neither (`na_routing_step_pair`): an attempt to a person she added names no step. */
    routingResolutionId: zod_1.z.uuid().nullable(),
    routingStep: zod_1.z.number().int().nullable(),
})
    .meta({ id: "AsOfAttempt", description: "One attempt, with the resolution and step it named" });
exports.alertAsOfSchema = zod_1.z
    .strictObject({
    rpmAlertId: zod_1.z.uuid(),
    alertState: zod_1.z.enum(rpm_enums_js_1.alertState.enumValues),
    openedAt: zod_1.z.iso.datetime(),
    /** The zone the lane was fixed in, where the save recorded one. */
    facilityZoneUsed: zod_1.z.string().nullable(),
    /** The alert's pinned set first, then any other set a reading was scored under, by version. */
    bandSets: zod_1.z.array(exports.asOfBandSetSchema),
    /** Null only for an alert whose lane was never fixed (`ra_lane_pins_versions`). */
    facilityProfile: exports.asOfFacilityProfileSchema.nullable(),
    /** Every resolution logged with the alert, in `sequence_no` order. */
    resolutions: zod_1.z.array(exports.asOfResolutionSchema),
    /** Every attempt on the alert, in `sequence_no` order. */
    attempts: zod_1.z.array(exports.asOfAttemptSchema),
})
    .meta({
    id: "AlertAsOf",
    description: "The band rows, the facility profile and the routing that governed one alert, as they were",
});
//# sourceMappingURL=rpm-alert-history.js.map